import Image from 'next/image';
import {
  siAnthropic,
  siBigcommerce,
  siBox,
  siBrex,
  siDropbox,
  siGoogle,
  siGooglesheets,
  siIntuit,
  siMake,
  siNotion,
  siVenmo,
  siWise,
  siWoocommerce,
  siWordpress,
} from 'simple-icons';
import { getIntegrationBrand } from '@/lib/brand/integration-brands';
import type { IntegrationPlatform } from '@/lib/types/integrations';
import { cn } from '@/lib/utils';
import { PlatformLogo } from './PlatformLogo';

export type BrandLogoName =
  | IntegrationPlatform
  | 'google-sheets'
  | 'notion'
  | 'anthropic'
  | 'venmo'
  | 'brex'
  | 'woocommerce'
  | 'bigcommerce'
  | 'wordpress'
  | 'google'
  | 'intuit'
  | 'wise'
  | 'make'
  | 'dropbox'
  | 'box'
  | 'freshbooks'
  | 'shippo'
  | 'webhooks'
  | 'mercury';

interface BrandLogoProps {
  name: BrandLogoName | (string & {});
  alt?: string;
  size?: number;
  className?: string;
}

// Additional supported tools outside the canonical connector catalog.
const TOOL_ICONS = {
  'google-sheets': siGooglesheets,
  notion: siNotion,
  anthropic: siAnthropic,
  venmo: siVenmo,
  brex: siBrex,
  woocommerce: siWoocommerce,
  bigcommerce: siBigcommerce,
  wordpress: siWordpress,
  google: siGoogle,
  intuit: siIntuit,
  wise: siWise,
  make: siMake,
  dropbox: siDropbox,
  box: siBox,
};
const TOOL_ASSETS = new Set(['freshbooks', 'shippo', 'webhooks', 'mercury']);

/** Canonical integrations use PlatformLogo; other marketing tools retain their assets. */
export function BrandLogo({ name, alt, size = 20, className }: BrandLogoProps) {
  const slug = name.trim().toLowerCase();
  if (getIntegrationBrand(slug)) {
    return (
      <PlatformLogo
        platform={slug}
        alt={alt}
        size={size}
        className={className}
      />
    );
  }
  if (TOOL_ASSETS.has(slug)) {
    return (
      <Image
        src={`/logos/integrations/${slug}.svg`}
        alt={alt ?? slug}
        className={cn('inline-block', className)}
        width={400}
        height={300}
        style={{
          height: size,
          width: 'auto',
          maxWidth: '100%',
          objectFit: 'contain',
        }}
        unoptimized
      />
    );
  }
  const icon = Object.hasOwn(TOOL_ICONS, slug)
    ? TOOL_ICONS[slug as keyof typeof TOOL_ICONS]
    : undefined;
  if (icon) {
    const label = alt ?? icon.title;
    return (
      <svg
        role={label ? 'img' : undefined}
        aria-label={label || undefined}
        aria-hidden={!label || undefined}
        viewBox="0 0 24 24"
        width={size}
        height={size}
        fill={`#${icon.hex}`}
        className={cn('inline-block', className)}
      >
        {label && <title>{label}</title>}
        <path d={icon.path} />
      </svg>
    );
  }
  return (
    <PlatformLogo platform={name} alt={alt} size={size} className={className} />
  );
}
